import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { BarChart3, Filter, Download, RefreshCw, Clock } from 'lucide-react';
import {
  AdminClientApi,
  ClientUserInterface,
  ClientBelief,
  ClientThoughtWithAuthor,
} from '../../Admin-ClientLibrary';
import { getApiUrl } from '../../config/api';
import { useRecentEntities } from '../../contexts/RecentEntitiesContext';
import { RecentEntity } from '../../types/RecentEntities';
import './BeliefAnalytics.css';

// Initialize the Admin Client Library
const getToken = (): string => {
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

interface CategoryData {
  categoryName: string;
  Believe: number;
  Unsure: number;
  Disbelieve: number;
}

interface BeliefAnalyticsResponse {
  success: boolean;
  categoryData: CategoryData[];
  message?: string;
}

const BeliefAnalytics: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { recentEntities } = useRecentEntities();

  // Filter states
  const [thoughtId, setThoughtId] = useState(searchParams.get('thoughtId') || '');
  const [category, setCategory] = useState<'Sex' | 'Location' | 'Age'>(
    (searchParams.get('category') as 'Sex' | 'Location' | 'Age') || 'Location'
  );
  const [startDate, setStartDate] = useState(searchParams.get('startDate') || '');
  const [endDate, setEndDate] = useState(searchParams.get('endDate') || '');
  const [withBeliefThoughtId, setWithBeliefThoughtId] = useState(
    searchParams.get('withBeliefThoughtId') || ''
  );
  const [withBeliefPosition, setWithBeliefPosition] = useState<'believe' | 'unsure' | 'disbelieve'>(
    (searchParams.get('withBeliefPosition') as 'believe' | 'unsure' | 'disbelieve') || 'believe'
  );

  // Data states
  const [data, setData] = useState<CategoryData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Recent dropdown state
  const [openRecentDropdown, setOpenRecentDropdown] = useState<string | null>(null);

  // Update URL params when filters change
  const updateUrlParams = (newParams: Record<string, string>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });
    setSearchParams(params);
  };

  // Load data from API
  const loadData = async () => {
    if (!thoughtId) {
      setError('Thought ID is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const requestBody: any = {
        thoughtId: parseInt(thoughtId),
        category,
      };

      if (startDate) requestBody.startDate = startDate;
      if (endDate) requestBody.endDate = endDate;
      if (withBeliefThoughtId && withBeliefPosition) {
        requestBody.withBelief = {
          thoughtId: parseInt(withBeliefThoughtId),
          position: withBeliefPosition,
        };
      }

      const response = await adminApi.getBeliefAnalytics(requestBody);
      const result: BeliefAnalyticsResponse = response;

      if (result.success) {
        setData(result.categoryData);
      } else {
        setError(result.message || 'Failed to load data');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters and update URL
  const applyFilters = () => {
    const newParams: Record<string, string> = {
      thoughtId,
      category,
    };

    if (startDate) newParams.startDate = startDate;
    if (endDate) newParams.endDate = endDate;
    if (withBeliefThoughtId) newParams.withBeliefThoughtId = withBeliefThoughtId;
    if (withBeliefPosition) newParams.withBeliefPosition = withBeliefPosition;

    updateUrlParams(newParams);
    loadData();
  };

  // Clear filters
  const clearFilters = () => {
    setThoughtId('');
    setCategory('Location');
    setStartDate('');
    setEndDate('');
    setWithBeliefThoughtId('');
    setWithBeliefPosition('believe');
    setSearchParams({});
    setData([]);
    setError(null);
  };

  // Recent entities helpers
  const getFilteredRecentEntities = (type: 'user' | 'belief' | 'thought'): RecentEntity[] => {
    return recentEntities.filter((entity) => entity.type === type);
  };

  const handleRecentEntityClick = (entity: RecentEntity, filterField: string): void => {
    if (filterField === 'thoughtId') {
      setThoughtId(entity.id.toString());
    } else if (filterField === 'withBeliefThoughtId') {
      setWithBeliefThoughtId(entity.id.toString());
    }
    setOpenRecentDropdown(null);
  };

  const toggleRecentDropdown = (dropdownType: string): void => {
    setOpenRecentDropdown(openRecentDropdown === dropdownType ? null : dropdownType);
  };

  const getEntityDisplayText = (entity: RecentEntity): string => {
    switch (entity.type) {
      case 'thought':
        const thoughtData = entity.data as ClientThoughtWithAuthor;
        return thoughtData.text || `Thought ${entity.id}`;
      case 'belief':
        const beliefData = entity.data as ClientBelief;
        return `Belief ${entity.id}`;
      case 'user':
        const userData = entity.data as ClientUserInterface;
        return userData.name || `User ${entity.id}`;
      default:
        return `Entity ${entity.id}`;
    }
  };

  // Close recent dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.recent-dropdown-container')) {
        setOpenRecentDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load data on component mount and when URL params change
  useEffect(() => {
    const urlThoughtId = searchParams.get('thoughtId');
    const urlCategory = searchParams.get('category') as 'Sex' | 'Location' | 'Age';
    const urlStartDate = searchParams.get('startDate');
    const urlEndDate = searchParams.get('endDate');
    const urlWithBeliefThoughtId = searchParams.get('withBeliefThoughtId');
    const urlWithBeliefPosition = searchParams.get('withBeliefPosition') as
      | 'believe'
      | 'unsure'
      | 'disbelieve';

    if (urlThoughtId) {
      setThoughtId(urlThoughtId);
      if (urlCategory) setCategory(urlCategory);
      if (urlStartDate) setStartDate(urlStartDate);
      if (urlEndDate) setEndDate(urlEndDate);
      if (urlWithBeliefThoughtId) setWithBeliefThoughtId(urlWithBeliefThoughtId);
      if (urlWithBeliefPosition) setWithBeliefPosition(urlWithBeliefPosition);

      loadData();
    }
  }, [searchParams]);

  // Calculate chart dimensions
  const maxValue = Math.max(...data.map((item) => item.Believe + item.Unsure + item.Disbelieve), 1);
  const chartHeight = 400;

  // RecentDropdown component
  const RecentDropdown: React.FC<{
    type: 'user' | 'belief' | 'thought';
    dropdownKey: string;
    filterField: string;
    placeholder: string;
  }> = ({ type, dropdownKey, filterField, placeholder }) => {
    const filteredEntities = getFilteredRecentEntities(type);
    const isOpen = openRecentDropdown === dropdownKey;

    return (
      <div className="recent-dropdown-container">
        <button
          type="button"
          className="recent-button"
          onClick={() => toggleRecentDropdown(dropdownKey)}
          disabled={filteredEntities.length === 0}
        >
          <Clock size={14} />
          Recent
        </button>
        {isOpen && filteredEntities.length > 0 && (
          <div className="recent-dropdown">
            <div className="recent-dropdown-header">
              Recent {type === 'user' ? 'Users' : type === 'belief' ? 'Beliefs' : 'Thoughts'}
            </div>
            <ul className="recent-dropdown-list">
              {filteredEntities.slice(0, 5).map((entity) => (
                <li
                  key={`${entity.type}-${entity.id}`}
                  className="recent-dropdown-item"
                  onClick={() => handleRecentEntityClick(entity, filterField)}
                >
                  <span className="recent-entity-id">ID: {entity.id}</span>
                  <span className="recent-entity-text">{getEntityDisplayText(entity)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="belief-analytics-page">
      <div className="belief-analytics-header">
        <div className="header-content">
          <h2>Belief Analytics</h2>
          <p>Analyze belief patterns across demographic categories</p>
        </div>
        <div className="header-actions">
          <button className="refresh-btn" onClick={loadData} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filters-header">
          <Filter size={20} />
          <h3>Filters</h3>
        </div>

        <div className="filters-grid">
          <div className="filter-group">
            <label htmlFor="thoughtId">Thought ID *</label>
            <input
              id="thoughtId"
              type="number"
              value={thoughtId}
              onChange={(e) => setThoughtId(e.target.value)}
              placeholder="Enter thought ID"
              required
            />
            <RecentDropdown
              type="thought"
              dropdownKey="thoughtId"
              filterField="thoughtId"
              placeholder="Recent Thoughts"
            />
          </div>

          <div className="filter-group">
            <label htmlFor="category">Category</label>
            <select
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value as 'Sex' | 'Location' | 'Age')}
            >
              <option value="Location">Location (Country)</option>
              <option value="Sex">Sex</option>
              <option value="Age">Age</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="startDate">Start Date</label>
            <input
              id="startDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="endDate">End Date</label>
            <input
              id="endDate"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="withBeliefThoughtId">Filter by Belief (Thought ID)</label>
            <input
              id="withBeliefThoughtId"
              type="number"
              value={withBeliefThoughtId}
              onChange={(e) => setWithBeliefThoughtId(e.target.value)}
              placeholder="Optional: Filter users with specific belief"
            />
            <RecentDropdown
              type="thought"
              dropdownKey="withBeliefThoughtId"
              filterField="withBeliefThoughtId"
              placeholder="Recent Thoughts"
            />
          </div>

          <div className="filter-group">
            <label htmlFor="withBeliefPosition">Belief Position</label>
            <select
              id="withBeliefPosition"
              value={withBeliefPosition}
              onChange={(e) =>
                setWithBeliefPosition(e.target.value as 'believe' | 'unsure' | 'disbelieve')
              }
            >
              <option value="believe">Believe</option>
              <option value="unsure">Unsure</option>
              <option value="disbelieve">Disbelieve</option>
            </select>
          </div>
        </div>

        <div className="filters-actions">
          <button className="apply-btn" onClick={applyFilters} disabled={loading || !thoughtId}>
            Apply Filters
          </button>
          <button className="clear-btn" onClick={clearFilters}>
            Clear Filters
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {/* Chart Section */}
      <div className="chart-section">
        <div className="chart-header">
          <h3>Belief Distribution by {category}</h3>
          {data.length > 0 && (
            <div className="chart-legend">
              <div className="legend-item">
                <span className="legend-color believe"></span>
                <span>Believe</span>
              </div>
              <div className="legend-item">
                <span className="legend-color unsure"></span>
                <span>Unsure</span>
              </div>
              <div className="legend-item">
                <span className="legend-color disbelieve"></span>
                <span>Disbelieve</span>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="loading-container">
            <RefreshCw size={32} className="spinning" />
            <p>Loading data...</p>
          </div>
        ) : data.length > 0 ? (
          <div className="chart-container">
            <div className="chart-bars">
              {data.map((item, index) => {
                const total = item.Believe + item.Unsure + item.Disbelieve;
                const believeHeight = (item.Believe / maxValue) * chartHeight;
                const unsureHeight = (item.Unsure / maxValue) * chartHeight;
                const disbelieveHeight = (item.Disbelieve / maxValue) * chartHeight;

                return (
                  <div key={index} className="chart-bar-group">
                    <div className="bar-container">
                      <div className="bar-stack">
                        <div
                          className="bar-segment believe"
                          style={{ height: `${believeHeight}px` }}
                          title={`Believe: ${item.Believe}`}
                        ></div>
                        <div
                          className="bar-segment unsure"
                          style={{ height: `${unsureHeight}px` }}
                          title={`Unsure: ${item.Unsure}`}
                        ></div>
                        <div
                          className="bar-segment disbelieve"
                          style={{ height: `${disbelieveHeight}px` }}
                          title={`Disbelieve: ${item.Disbelieve}`}
                        ></div>
                      </div>
                    </div>
                    <div className="bar-label">{item.categoryName}</div>
                    <div className="bar-total">{total}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <BarChart3 size={48} />
            <p>No data available. Apply filters to see results.</p>
          </div>
        )}
      </div>

      {/* Data Table */}
      {data.length > 0 && (
        <div className="data-table-section">
          <h3>Detailed Data</h3>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Believe</th>
                  <th>Unsure</th>
                  <th>Disbelieve</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => (
                  <tr key={index}>
                    <td>{item.categoryName}</td>
                    <td className="believe-cell">{item.Believe}</td>
                    <td className="unsure-cell">{item.Unsure}</td>
                    <td className="disbelieve-cell">{item.Disbelieve}</td>
                    <td className="total-cell">{item.Believe + item.Unsure + item.Disbelieve}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BeliefAnalytics;
